import { ReportStore } from 'modules/report/stores';
import { excludedColumns, reportCategoryColumns, reportTimeSpanOptions } from 'common/constants/reportConstants';
import { BaseViewStore, CoreUserStore } from 'core/stores';
import { action, makeObservable, observable } from 'mobx';
import { isSome, isSuperAdminOrganization, isWarehouse, translate } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import { ApplicationStore } from 'platform/application/stores';
import { lookupRouteName, organizationTypes, sourceOfChange } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import { FilterParams } from 'core/models';
import { localStorageHandler } from 'core/services';
import { ReportForm, ReportFilterForm } from 'modules/report/forms';
import {
	ChannelService,
	OrderService,
	FulfillmentOrderItemService,
	ProductService,
	CarrierServiceService,
	CarrierService,
	ApplicationService,
	ShipmentInfoService,
	PalletService,
	PickingBatchService,
	PackingSlipService,
	FulfillmentOrderService,
	FulfillmentChannelOrderService,
} from 'common/data';
import moment from 'moment';
import React from 'react';
import _ from 'lodash';

class ReportEditViewStore extends BaseViewStore {
	@observable columns = [];
	@observable selectedColumn = {
		selectedValues: [],
		selectedValue: null,
	};
	@observable timeSpanOptions = reportTimeSpanOptions;
	scrollContainerRef = React.createRef();
	pageNumber = 1;
	categoryOptions = [];
	services = [];
	isSuperAdmin = false;
	form = new ReportForm({
		onSuccess: async () => {
			await this.onSaveReport();
		},
	});
	filterForm = new ReportFilterForm();

	get organizationId() {
		return this.organizationDropdownStore ? this.organizationDropdownStore.value.value : null;
	}

	get organizationTypeAbrv() {
		return this.organizationDropdownStore
			? this.organizationDropdownStore.value.extra.organizationType.abrv.toUpperCase()
			: null;
	}

	get isTemplate() {
		return (
			this.rootStore.routerStore.routerState.routeName === 'master.app.main.report.list.add-template' ||
			(this.report != null && this.report.reportType.abrv == 'template')
		);
	}

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.rootStore = rootStore;
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.reportStore = new ReportStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.isEdit = this.rootStore.routerStore.routerState.routeName === 'master.app.main.report.list.edit';
		this.currentOrganizationId = this.rootStore.applicationStore.currentOrganization.id;
		this.services = [
			new FulfillmentOrderService(this.rootStore.app.baasic.apiClient),
			new ChannelService(this.rootStore.app.baasic.apiClient),
			new OrderService(this.rootStore.app.baasic.apiClient),
			new FulfillmentOrderItemService(this.rootStore.app.baasic.apiClient),
			new ProductService(this.rootStore.app.baasic.apiClient),
			new CarrierServiceService(this.rootStore.app.baasic.apiClient),
			new CarrierService(this.rootStore.app.baasic.apiClient),
			new ApplicationService(this.rootStore.app.baasic.apiClient),
			new ShipmentInfoService(this.rootStore.app.baasic.apiClient),
			new PalletService(this.rootStore.app.baasic.apiClient),
			new PickingBatchService(this.rootStore.app.baasic.apiClient),
			new PackingSlipService(this.rootStore.app.baasic.apiClient),
			new FulfillmentChannelOrderService(this.rootStore.app.baasic.apiClient),
		];
		this.initialize();
	}

	@action
	initialize = async () => {
		this.suspend();
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');
		if (this.isSuperAdmin) {
			this.organizationDropdownStore = new BaasicDropdownViewStore({
				field: this.form.$('organizationId'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					excludeSuperAdminOrganization: true,
					embed: ['organizationType'],
					page: 1,
					rpp: 100,
				},
				isRequired: true,
				onChangeCallback: this.onOrganizationChange,
			});
		}
		this.reportFrequencyDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('frequencyId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.reportFrequency,
			isRequired: true,
		});
		this.reportCategoryDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('reportCategoryId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.reportCategory,
			onChangeCallback: this.onCategoryChange,
			isRequired: true,
		});
		this.userDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('reportUserIds'),
			store: new CoreUserStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'displayName',
			},
			filterProperties: {
				embed: ['coreUser'],
				fetchSuperAdminUsers: this.isSuperAdmin,
			},
			isRequired: true,
			isMulti: true,
			filterCallback: this.cleanDisplayName,
		});
		this.reportTemplateDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('templateId'),
			store: new ReportStore(this.rootStore),
			filterProperties: {
				embed: [
					'template',
					'template.users',
					'template.users.coreUser',
					'reportFrequency',
					'reportCategory',
					'users',
					'users.coreUser',
				],
				templateOnly: true,
				isActive: true,
			},
			onChangeCallback: this.getTemplateData,
		});
		if (this.isEdit) {
			await this.getReport();
		} else {
			if (this.isSuperAdmin) {
				if (!this.isEdit) {
					const organization = localStorageHandler.get('selectedOrganization');
					if (organization) {
						this.organizationDropdownStore.setValue(organization);
						await this.organizationDropdownStore.reload();
					}
				}
			}
		}

		// If is template and is create new template, then all columns are set as selected by default
		if (this.isTemplate && !this.isEdit) {
			_.forEach(this.columns, column => (column.isSelected = true));
		}

		this.resume();
	};

	@action
	getReport = async () => {
		this.suspend();
		try {
			this.report = await this.reportStore.getReport(this.rootStore.routerStore.routerState.params.id, {
				embed: [
					'organization',
					'organization.organizationType',
					'template',
					'template.users',
					'template.users.coreUser',
					'reportFrequency',
					'users',
					'users.coreUser',
					'reportType',
					'reportCategory',
					'organization',
					'organization.organizationType',
				],
			});

			const {
				reportFrequency,
				users,
				json,
				template,
				scheduledDate,
				sendAt,
				name,
				isActive,
				reportCategory,
				reportType,
			} = this.report;

			this.form.$('isActive').value = isActive;
			this.form.$('name').value = name;
			this.form.$('scheduledDate').value = scheduledDate ? moment(scheduledDate).format('L') : null;
			this.form.$('sendAt').value = sendAt ? this.convertTimeFromUTC(sendAt) : null;
			this.form.$('reportTypeId').value = reportType.id;
			if (this.isSuperAdmin) {
				this.organizationDropdownStore.setValue(this.report.organization);
				await this.organizationDropdownStore.reload();
			}
			if (!this.isTemplate && template) {
				this.reportTemplateDropdownStore.setValue(template);
			}
			this.reportFrequencyDropdownStore.setValue(reportFrequency);
			this.reportCategoryDropdownStore.setValue(reportCategory);

			this.userDropdownStore.setValue(
				_.map(users, user => {
					return {
						value: user.userId,
						label: user.displayName.split('-')[0],
					};
				})
			);
			this.columns = JSON.parse(json);
			this.onSelectColumn(this.columns[0]); // First column in the list is set as active by default
		} catch (err) {
			this.rootStore.notificationStore.error(err);
		}
		this.resume();
	};

	@action
	getColumnValues = async (column, search = '') => {
		this.isDataLoading = true;

		// If all data is fetched there is no need to fetch it again, all data is stored in column properties until being saved
		if (!column.isAllDataFetched) {
			const { sortBy, filterKey, columnKey, lookupConstant, getDistinctValues } = column;
			const isLookup = isSome(lookupConstant);
			let filter = new FilterParams();
			let response = null;
			if (isLookup) {
				filter = Object.assign(filter, {
					orderBy: 'name',
					orderDirection: 'asc',
					search: search,
				});
				response = await this.lookupStore.getLookupService(lookupConstant).find(filter);
			} else if (isSome(column.service)) {
				const serviceIdx = _.findIndex(this.services, item => {
					return item.constructor.name.toLowerCase() == column.service.toLowerCase();
				});
				if (getDistinctValues) {
					response = await this.services[serviceIdx].getDistinctValues({
						fieldName: columnKey,
						pageNumber: this.pageNumber,
						search: search,
					});
				} else {
					const checkForWarehouseLogin =
						columnKey === 'warehouse' ||
						columnKey === 'orderNumber' ||
						columnKey === 'pallet' ||
						columnKey === 'fulfillmentOrder.order.orderNumber' ||
						columnKey === 'fulfillmentOrder.order.channel.name';
					const isWarehouseUser =
						checkForWarehouseLogin &&
						(this.isWarehouse || this.organizationTypeAbrv == organizationTypes.warehouse.toUpperCase());

					filter = Object.assign(filter, {
						isDeleted: false,
						searchFields: filterKey ? filterKey : columnKey,
						search: search != '' ? search : null,
						pageNumber: this.pageNumber,
						orderBy: filterKey ? filterKey : columnKey,
						orderDirection: sortBy ? sortBy : 'asc', // Default sort by is ascending
						warehouseOnly: columnKey === 'warehouse',
						isWarehouse: isWarehouseUser,
						excludeShipmentsWithNoCarrier: column.excludeShipmentsWithNoCarrier,
						sellerOnly: column.sellerOnly,
						shippedOnly: column.shippedOnly,
						sellerIds:
							this.organizationId &&
							this.organizationTypeAbrv == organizationTypes.seller.toUpperCase() &&
							columnKey !== 'warehouse'
								? [this.organizationId || this.currentOrganizationId]
								: [],
						warehouseIds:
							(this.organizationId && this.organizationTypeAbrv == organizationTypes.warehouse.toUpperCase()) ||
							isWarehouseUser
								? [this.organizationId || this.currentOrganizationId]
								: [],
					});
					if (columnKey === 'orderNumber') {
						filter.checkPartiallyFulfillMixedOrders = true;
						filter.getOnlySplitted = true;
					}
					response = await this.services[serviceIdx].find(filter);
				}
			}
			// Get distinct values
			if (column.getDistinctValues && response && response.data && response && response.data.length) {
				// Check if user switched columns without waiting for response to finish
				if (this.selectedColumn.columnKey == column.columnKey) {
					this.setResponseData(response.data, column);
					// If response length is lesser than 100 then all is fetched because pageSize is 100 per request
					if (response.data.length < this.rpp || column.lookupConstant) {
						this.selectedColumn.isAllDataFetched = true;
					}
				}
			}
			// Get lookups/regular data
			else if (response && response.data && response.data.item && response.data.item.length) {
				// Check If user switched columns without waiting for response to finish
				if (this.selectedColumn.columnKey == column.columnKey) {
					this.setResponseData(response.data.item, column);
					if (response.data.totalRecords === this.selectedColumn.allItemValues.length || column.lookupConstant) {
						this.selectedColumn.isAllDataFetched = true;
					}
				}
			} else if (this.pageNumber === 1) {
				this.selectedColumn.allItemValues = [];
			}
		}
		this.isDataLoading = false;
	};

	@action.bound
	onColumnFilterScroll = async () => {
		const { scrollTop, scrollHeight, offsetHeight } = this.scrollContainerRef.current;
		if (scrollTop > 0 && scrollTop + offsetHeight + 1 >= scrollHeight && !this.selectedColumn.isAllDataFetched) {
			this.pageNumber++;
			await this.getColumnValues(this.selectedColumn, this.filterForm.$('search').value);
		}
	};

	@action
	setResponseData = (responseData, column) => {
		responseData = responseData.filter(
			responseItem => !this.selectedColumn.allItemValues.some(item => item.id === responseItem.id)
		);
		this.selectedColumn.allItemValues = _.concat(
			this.selectedColumn.allItemValues,
			_.map(responseData, data => {
				const existingItem = _.find(this.selectedColumn.selectedValues, {
					id: column.getDistinctValues ? data : data.id,
				});

				return existingItem
					? {
							id: existingItem.id,
							name: existingItem.name,
							isSelected: existingItem.isSelected,
					  }
					: {
							id: column.getDistinctValues ? data : data.id,
							name: column.getDistinctValues
								? data
								: column.filterKey
								? data[column.filterKey]
								: data[column.columnKey],
							isSelected: this.selectedColumn.isSelected,
					  };
			})
		);

		this.selectedColumn.selectedValues = [
			...this.selectedColumn.selectedValues, // Already existing items in selectedValues
			...this.selectedColumn.allItemValues.filter(
				// Items that exist in allItemValues, but not in selectedValues array
				item => !this.selectedColumn.selectedValues.some(selectedValue => selectedValue.id === item.id)
			),
		];

		if (this.selectedColumn.allItemValues) {
			let selectedValuesToAdd = this.selectedColumn.selectedValues.filter(
				// Retrieve selected values from beyond the initial 100 results
				v => v.isSelected && !this.selectedColumn.allItemValues.some(item => item.id === v.id)
			);
			this.selectedColumn.allItemValues.push(...selectedValuesToAdd); // add to current result set

			// apply sorting - selected values first
			this.selectedColumn.allItemValues = this.selectedColumn.allItemValues.sort((a, b) => {
				if (a.isSelected && !b.isSelected) {
					return -1;
				} else if (!a.isSelected && b.isSelected) {
					return 1;
				} else {
					return 0;
				}
			});
		}
	};

	@action
	getTemplateData = template => {
		if (!this.isEdit) {
			if (template) {
				const { users, json, reportCategory, reportFrequency, scheduledDate, sendAt } = template.extra;
				this.userDropdownStore.setValue(
					_.map(users, user => {
						return {
							value: user.userId,
							label: user.displayName.split('-')[0],
						};
					})
				);
				this.reportFrequencyDropdownStore.setValue(reportFrequency);
				this.reportCategoryDropdownStore.setValue(reportCategory);
				this.form.$('scheduledDate').value = scheduledDate ? moment(scheduledDate).format('L') : null;
				this.form.$('sendAt').value = sendAt ? this.convertTimeFromUTC(sendAt) : null;
				this.columns = JSON.parse(json);
				this.onSelectColumn(this.columns[0]); // First column in the list is set as active by default
			} else {
				_.each(
					[this.userDropdownStore, this.reportFrequencyDropdownStore, this.reportCategoryDropdownStore],
					dropdownStore => {
						dropdownStore.clear();
					}
				);
				this.form.$('scheduledDate').clear();
				this.form.$('sendAt').clear();
				this.columns = [];
			}
		}
	};

	@action
	onSelectColumnCheckbox = async column => {
		column.isSelected = !column.isSelected;
		_.forEach(column.allItemValues, itemValue => {
			itemValue.isSelected = column.isSelected;
		});
		_.forEach(column.selectedValues, itemValue => {
			itemValue.isSelected = column.isSelected;
		});
		column.isPartiallySelected = false;
	};

	@action
	onSelectColumnItemCheckbox = async (column, item) => {
		item.isSelected = !item.isSelected;
		_.forEach(this.selectedColumn.selectedValues, selectedValue => {
			if (selectedValue.id === item.id) {
				selectedValue.isSelected = item.isSelected;
			}
		});
		const allSelected =
			_.every(column.allItemValues, { isSelected: true }) && _.every(column.selectedValues, { isSelected: true });
		const noneSelected =
			_.every(column.allItemValues, { isSelected: false }) && _.every(column.selectedValues, { isSelected: false });
		column.isPartiallySelected = !allSelected && !noneSelected;
	};

	@action
	onSelectColumn = async selectedColumn => {
		this.selectedColumn = selectedColumn;

		// isActive is used only for css, it gives blue line on selected column
		_.forEach(this.columns, column => {
			if (this.selectedColumn.columnKey === column.columnKey) {
				column.isActive = true;
			} else {
				column.isActive = false;
			}
		});

		if (this.selectedColumn.isFilterable && this.selectedColumn.filterType === 1) {
			this.pageNumber = 1;
			this.rpp = 100;
			this.filterForm.$('search').value = '';
			this.filterForm.$('contains').value = this.selectedColumn.searchContains;
			this.selectedColumn.allItemValues = [];
			this.selectedColumn.isAllDataFetched = false;
			await this.getColumnValues(this.selectedColumn);
		}
		if (this.selectedColumn.isFilterable && this.selectedColumn.filterType === 4) {
			this.filterForm.$('specificNumber').value = this.selectedColumn.specificNumber;
			this.filterForm.$('numberRangeStart').value = this.selectedColumn.numberRangeStart;
			this.filterForm.$('numberRangeEnd').value = this.selectedColumn.numberRangeEnd;
		}
	};

	@action
	handleSearchColumnItemsDebounced = _.debounce((column, search) => {
		this.handleSearchColumnItems(column, search.trim());
	}, 600);

	@action
	handleSearchColumnItems = (selectedColumn, search) => {
		this.filterForm.$('search').value = search;
		this.selectedColumn.allItemValues = [];
		this.pageNumber = 1;
		selectedColumn.isAllDataFetched = false;
		this.getColumnValues(selectedColumn, search);
	};

	@action
	handleColumnsOrderChange = (dragIndex, hoverIndex) => {
		if (dragIndex < 0 || hoverIndex < 0 || dragIndex >= this.columns.length || hoverIndex >= this.columns.length) {
			return;
		}

		const updatedColumns = [...this.columns];
		const [draggedItem] = updatedColumns.splice(dragIndex, 1);

		updatedColumns.splice(hoverIndex, 0, draggedItem);

		this.columns = updatedColumns;
	};

	@action
	onDateTimeOptionChange = selectedOption => {
		_.forEach(this.selectedColumn.dateTimeOptions, option => {
			if (option.title === selectedOption.title) {
				option.checked = true;
			} else {
				option.checked = false;
			}
		});
	};

	@action
	onUseSpecificDateChange = value => {
		this.selectedColumn.useSpecificDate = value;
	};

	@action
	onSearchContainsFilterChange = value => {
		this.selectedColumn.searchContains = value;
	};

	@action
	onNumericSpecificFilterChange = value => {
		if (this.filterForm.isValid) {
			this.selectedColumn.specificNumber = value;
		} else {
			this.selectedColumn.specificNumber = null;
		}
	};

	@action
	onNumericMinFilterChange = value => {
		if (this.filterForm.isValid) {
			this.selectedColumn.numberRangeStart = value;
		} else {
			this.selectedColumn.numberRangeStart = null;
		}
	};

	@action
	onNumericMaxFilterChange = value => {
		if (this.filterForm.isValid) {
			this.selectedColumn.numberRangeEnd = value;
		} else {
			this.selectedColumn.numberRangeEnd = null;
		}
	};

	@action
	onSpecificDayClick = value => {
		this.selectedColumn.specificDate = moment(value).format('YYYY-MM-DD');
	};

	@action
	onToggleChange = type => {
		if (type === 1) {
			this.selectedColumn.selectedValue = true;
		} else if (type === 2) {
			this.selectedColumn.selectedValue = null;
		} else if (type === 3) {
			this.selectedColumn.selectedValue = false;
		}
	};

	@action
	onSaveAndSendReport = async () => {
		this.form.validate().then(async ({ isValid }) => {
			if (isValid) {
				await this.onSaveReport(true);
			} else {
				this.form.showErrors(true);
			}
		});
	};

	@action
	onSaveReport = async (saveAndSend = false) => {
		this.suspend();
		try {
			const { reportUserIds, organizationId, sendAt, scheduledDate, ...otherValues } = this.form.values();
			const users = this.getUsers(reportUserIds);
			this.prepareColumnData();

			const reportData = {
				id: this.isEdit ? this.rootStore.routerStore.routerState.params.id : undefined,
				json: JSON.stringify(this.columns),
				isTemplate: this.isTemplate,
				organizationId: this.isSuperAdmin ? organizationId : this.currentOrganizationId,
				reportUsers: users,
				scheduledDate: moment(scheduledDate, this.datePickerFormat),
				sendAt: this.convertTimeToUTC(sendAt),
				...otherValues,
			};

			if (this.isEdit) {
				await this.reportStore.updateReport(
					reportData,
					this.isTemplate ? sourceOfChange.updateReportTemplate : sourceOfChange.updateReport
				);
				this.rootStore.notificationStore.success([
					'REPORTS.UPDATESUCCESS',
					{ name: name, type: this.isTemplate ? 'template' : 'report' },
				]);
			} else {
				var createdReport = await this.reportStore.createReport(
					reportData,
					this.isTemplate ? sourceOfChange.createReportTemplate : sourceOfChange.createReport
				);
				this.rootStore.notificationStore.success([
					'REPORTS.CREATESUCCESS',
					{ name: name, type: this.isTemplate ? 'template' : 'report' },
				]);
			}
			this.resume();
			this.rootStore.routerStore.goTo('master.app.main.report.list'); // Return to Report List page after success
			if (saveAndSend) {
				await this.sendReport(this.isEdit ? reportData : createdReport[0]);
			}
		} catch (err) {
			this.rootStore.notificationStore.error([
				'REPORTS.CREATEERROR',
				{ type: this.isTemplate ? 'template' : 'report' },
			]);
		}
	};

	@action
	sendReport = async item => {
		const notificationId = this.rootStore.notificationStore.load(translate('REPORTS.SENDINGREPORT'));
		try {
			var isSuccess = await this.reportStore.sendReport({ reportId: item.id });
			if (isSuccess) {
				this.rootStore.notificationStore.success(['REPORTS.SENDSUCCESS', { name: item.name }]);
			} else {
				this.rootStore.notificationStore.error(['REPORTS.SENDERROR']);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['REPORTS.SENDERROR']);
		}
		this.rootStore.notificationStore.dismiss(notificationId);
	};

	@action
	onCategoryChange = selectedCategory => {
		if (selectedCategory) {
			switch (selectedCategory.label) {
				case 'Fulfillment Orders':
					this.columns = reportCategoryColumns.fulfillmentOrders;
					if (this.isWarehouse || this.organizationTypeAbrv == organizationTypes.warehouse.toUpperCase()) {
						this.columns = this.columns.filter(x =>
							excludedColumns.warehouseFulfillmentOrders.every(o => o != x.columnKey)
						);
					}
					break;
				case 'Marketplace Orders':
					this.columns = reportCategoryColumns.marketplaceOrders;
					break;
				case 'Fulfillment Shipments':
					this.columns = reportCategoryColumns.fulfillmentShipments;
					if (
						this.isWarehouse ||
						this.organizationTypeAbrv == organizationTypes.warehouse.toUpperCase() ||
						this.isSuperAdmin
					) {
						this.columns.push({
							filterType: 1,
							columnKey: 'seller.applicationTitle',
							title: 'SHIPMENTSTATUSVIEW.SELLER',
							service: 'applicationService',
							filterKey: 'applicationTitle',
							sellerOnly: true,
							isSearchable: true,
							isSelected: true,
							isPartiallySelected: false,
							isFilterable: true,
							allItemValues: [],
							selectedValues: [],
							searchContains: '',
						});
					}
					break;
				case 'Products':
					this.columns = reportCategoryColumns.products;
					break;
				case 'Containers':
					this.columns = reportCategoryColumns.containers;
					break;
				default:
					this.columns = [];
					break;
			}
			this.onSelectColumn(this.columns[0]); // First column in the list is set as active by default
		}
	};

	handleBackButtonClick = () => {
		this.rootStore.routerStore.goTo('master.app.main.report.list');
	};

	@action
	cleanDisplayName = users => {
		return _.map(users, user => {
			return {
				value: user.coreUserId,
				label: user.coreUser.displayName.split('-')[0],
				extra: user,
			};
		});
	};

	getUsers = reportUserIds => {
		return _.map(reportUserIds, id => {
			return {
				userId: id,
			};
		});
	};

	onOrganizationChange = async () => {
		if (this.organizationId) {
			if (!this.isEdit) {
				this.userDropdownStore.setValue(null);
				this.reportCategoryDropdownStore.setValue(null);
			}
			await this.userDropdownStore.updateFilter({ organizationIds: [this.organizationId] });
			if (!this.isTemplate) {
				await this.reportTemplateDropdownStore.updateFilter({
					organizationIds: [this.organizationId],
				});
			}
		}
	};

	prepareColumnData = () => {
		_.forEach(this.columns, column => {
			// If filter type is searchable
			if (column.filterType === 1) {
				if (column.searchContains && column.searchContains != '') {
					column.selectedValues = [];
				} else {
					if (
						// If all is selected
						column.selectedValues.length > 0 &&
						column.selectedValues.every(selectedValue => selectedValue.isSelected)
					) {
						column.isSelected = true;
						column.selectedValues = [];
					} else if (
						// If none is selected
						column.selectedValues.length > 0 &&
						column.selectedValues.every(selectedValue => !selectedValue.isSelected)
					) {
						column.isSelected = false;
						column.selectedValues = [];
					}
				}

				column.allItemValues = [];
				delete column.isAllDataFetched; // No need to store isAllDataFetched in json
			}
			delete column.isActive; // No need to store isActive in json
		});
	};

	convertTimeToUTC = time =>
		moment(`${moment().format('YYYY-MM-DD')} ${time}`)
			.utc()
			.format('HH:mm:ss');

	convertTimeFromUTC = utcTime => {
		const targetTimeZone = moment.tz.guess();
		const currentDate = moment().format('YYYY-MM-DD');
		return moment
			.utc(`${currentDate} ${utcTime}`)
			.tz(targetTimeZone)
			.format('HH:mm:ss');
	};
}

export default ReportEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/report/stores/ReportEditViewStore.js